/**
 * PDF Export for OE Summary Report
 *
 * Generates a formatted PDF containing:
 * - Organization and scope info header
 * - Date generated
 * - REPS quadrant scores (color-coded)
 * - OE Element breakdown table
 * - Active warnings list
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { REPS_COLORS, OE_ELEMENTS } from './constants';
import { getScoreColor } from './scoring';
import { normalizeThresholds, scoreBand } from './bands';

/**
 * Convert hex color to RGB array
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
}

/**
 * Get severity label for a score
 */
function getSeverityLabel(score, threshold = 6) {
  if (score == null) return 'N/A';
  return scoreBand(score, threshold).label;
}

/**
 * Generate PDF summary report of OE scores
 *
 * @param {Object} params
 * @param {Object} params.scores - REPS scores { reliability, efficiency, productivity, safety }
 * @param {Object|null} params.elementScores - Per-element scores keyed by element key
 * @param {Array} params.warnings - Array of warning objects { quadrant, score, threshold, message }
 * @param {Object} params.organization - Organization object { name, id }
 * @param {Object} params.scope - Current scope { type, id }
 * @param {Object} params.thresholds - Threshold values per quadrant
 */
export function generateOESummaryPDF({
  scores,
  elementScores,
  warnings = [],
  organization,
  scope,
  thresholds,
}) {
  const tN = normalizeThresholds(thresholds);
  const tShort = { R: tN.reliability, E: tN.efficiency, P: tN.productivity, S: tN.safety };
  const avgThreshold = (tN.reliability + tN.efficiency + tN.productivity + tN.safety) / 4;
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let y = margin;

  // ── Header ──────────────────────────────────────────────
  // PPI / REPSit branding
  doc.setFillColor(25, 118, 210); // MUI primary blue
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('REPSit', margin, 18);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Drift & Accumulation — OE Summary Report', margin, 28);

  // Date and org info on the right side of header
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  doc.setFontSize(9);
  doc.text(dateStr, pageWidth - margin, 18, { align: 'right' });

  if (organization?.name) {
    doc.text(organization.name, pageWidth - margin, 26, { align: 'right' });
  }

  // Scope info below header
  y = 48;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);

  const scopeLabel = scope?.type
    ? `Scope: ${scope.type.charAt(0).toUpperCase() + scope.type.slice(1)}${scope.id ? ` (${scope.id})` : ''}`
    : 'Scope: Organization-wide';
  doc.text(scopeLabel, margin, y);
  y += 10;

  // ── REPS Quadrant Scores ────────────────────────────────
  doc.setTextColor(33, 33, 33);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('REPS Quadrant Scores', margin, y);
  y += 8;

  const quadrants = [
    { key: 'reliability', label: 'Reliability', color: REPS_COLORS.reliability },
    { key: 'efficiency', label: 'Efficiency', color: REPS_COLORS.efficiency },
    { key: 'productivity', label: 'Productivity', color: REPS_COLORS.productivity },
    { key: 'safety', label: 'Safety', color: REPS_COLORS.safety },
  ];

  const cardWidth = (contentWidth - 9) / 4; // 3 gaps of 3mm
  const cardHeight = 35;

  quadrants.forEach((q, i) => {
    const x = margin + i * (cardWidth + 3);
    const score = scores ? scores[q.key] : null;
    const scoreColor = score != null ? getScoreColor(score, tN[q.key]) : '#9e9e9e';
    const severityLabel = getSeverityLabel(score, tN[q.key]);

    // Card background
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');

    // Top color stripe
    const [r, g, b] = hexToRgb(q.color);
    doc.setFillColor(r, g, b);
    doc.rect(x, y, cardWidth, 3, 'F');

    // Quadrant label — in its REPS color (P5-02, D09 image 4/5)
    doc.setTextColor(r, g, b);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(q.label.toUpperCase(), x + cardWidth / 2, y + 10, { align: 'center' });

    // Score value in the band color, "/10" in the report-title navy
    const [sr, sg, sb] = hexToRgb(scoreColor);
    const scoreText = score != null ? score.toFixed(1) : '—';
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const sw = doc.getTextWidth(scoreText);
    doc.setFontSize(9);
    const uw = doc.getTextWidth(' /10');
    const startX = x + cardWidth / 2 - (sw + uw) / 2;
    doc.setFontSize(18);
    doc.setTextColor(sr, sg, sb);
    doc.text(scoreText, startX, y + 22);
    doc.setFontSize(9);
    doc.setTextColor(31, 56, 100);
    doc.text(' /10', startX + sw, y + 22);

    // Severity as a color-coded badge
    if (score != null) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      const bw = doc.getTextWidth(severityLabel) + 8;
      doc.setFillColor(sr, sg, sb);
      doc.roundedRect(x + cardWidth / 2 - bw / 2, y + 25, bw, 7, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(severityLabel, x + cardWidth / 2, y + 30, { align: 'center' });
      doc.setFont('helvetica', 'normal');
    }

    // Threshold indicator
    if (thresholds && thresholds[q.key] != null) {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(6);
      doc.text(`Threshold: ${thresholds[q.key]}`, x + cardWidth / 2, y + 34, { align: 'center' });
    }
  });

  y += cardHeight + 12;

  // ── OE Element Breakdown Table ──────────────────────────
  doc.setTextColor(33, 33, 33);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('OE Element Breakdown', margin, y);
  y += 6;

  const tableHeaders = [['Element', 'Avg Score', 'R', 'E', 'P', 'S', 'Severity']];
  const tableRows = OE_ELEMENTS.map((element) => {
    const elScores = elementScores ? elementScores[element.key] : null;
    if (!elScores) {
      return [element.label, '—', '—', '—', '—', '—', 'N/A'];
    }
    const values = ['R', 'E', 'P', 'S'].map((k) => elScores[k] ?? 0);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return [
      element.label,
      avg.toFixed(1),
      (elScores.R ?? 0).toFixed(1),
      (elScores.E ?? 0).toFixed(1),
      (elScores.P ?? 0).toFixed(1),
      (elScores.S ?? 0).toFixed(1),
      getSeverityLabel(avg, avgThreshold),
    ];
  });

  doc.autoTable({
    startY: y,
    head: tableHeaders,
    body: tableRows,
    margin: { left: margin, right: margin },
    theme: 'grid',
    headStyles: {
      fillColor: [25, 118, 210],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 9,
      halign: 'center',
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 55 },
      1: { cellWidth: 20 },
      6: { cellWidth: 20 },
    },
    didParseCell: (data) => {
      // Color-code the Avg Score and Severity columns
      if (data.section === 'body') {
        const row = tableRows[data.row.index];
        if (!row) return;

        // Color the average score cell
        if (data.column.index === 1 && row[1] !== '—') {
          const avgScore = parseFloat(row[1]);
          const color = hexToRgb(getScoreColor(avgScore, avgThreshold));
          data.cell.styles.textColor = color;
          data.cell.styles.fontStyle = 'bold';
        }

        // Color the REPS cells (columns 2-5)
        if (data.column.index >= 2 && data.column.index <= 5 && row[data.column.index] !== '—') {
          const cellScore = parseFloat(row[data.column.index]);
          const color = hexToRgb(getScoreColor(cellScore, tShort[['R', 'E', 'P', 'S'][data.column.index - 2]]));
          data.cell.styles.textColor = color;
        }

        // Color the severity cell
        if (data.column.index === 6) {
          const severity = row[6];
          if (severity === 'Healthy') {
            data.cell.styles.textColor = hexToRgb('#2e7d32');
          } else if (severity === 'Monitor') {
            data.cell.styles.textColor = hexToRgb('#f9a825');
          } else if (severity === 'Warning') {
            data.cell.styles.textColor = hexToRgb('#c62828');
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    },
  });

  y = doc.lastAutoTable.finalY + 12;

  // ── Active Warnings ─────────────────────────────────────
  doc.setTextColor(33, 33, 33);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Active Warnings', margin, y);
  y += 6;

  if (warnings.length === 0) {
    doc.setTextColor(46, 125, 50); // green
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('No active warnings. All REPS scores are within thresholds.', margin, y + 4);
    y += 12;
  } else {
    const warningHeaders = [['Quadrant', 'Score', 'Threshold', 'Status']];
    const warningRows = warnings.map((w) => [
      w.quadrant.charAt(0).toUpperCase() + w.quadrant.slice(1),
      w.score.toFixed(1),
      String(w.threshold),
      'EXCEEDED',
    ]);

    doc.autoTable({
      startY: y,
      head: warningHeaders,
      body: warningRows,
      margin: { left: margin, right: margin },
      theme: 'grid',
      headStyles: {
        fillColor: [198, 40, 40], // red
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'left' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          data.cell.styles.textColor = [198, 40, 40];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Footer ──────────────────────────────────────────────
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

  doc.setTextColor(150, 150, 150);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Generated by REPSit.ai — AI-Powered Performance Intelligence',
    margin,
    pageHeight - 10
  );
  doc.text(dateStr, pageWidth - margin, pageHeight - 10, { align: 'right' });

  // Save the PDF
  const filename = `OE_Summary_${organization?.name ? organization.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
